import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useStore } from 'zustand';
import cableService from '@/widget/services/cableService';
import { useChatWidgetContext } from '@/widget/services/useChatWidgetContext';
import generateUUID from '@/lib/generateUUID';
import { storeGet } from '@/services/localStorageAdapter';

export default function useWidget() {
  const { store } = useChatWidgetContext();
  const {
    setWidgetSubscription, setContactRecognized, setContactRequested, accountId, userId, orgName,
  } = store.getState();
  const [connected, setConnected] = useState(false);
  const subscription = useStore(store, (state) => state.widgetSubscription);
  const contactRequested = useStore(store, (state) => state.contactRequested);
  const contactId = useStore(store, (state) => state.contactId);
  const uuid = useMemo(() => generateUUID(), []);

  const subscribeWidgetEvents = useCallback((cable) => {
    const widgetSubscription = cable.subscriptions.create(
      { channel: 'ChatWidgetChannel', key: uuid },
      {
        connected: () => {
          setWidgetSubscription(widgetSubscription);
          setConnected(true);
        },
        disconnected: () => {
          setConnected(false);
        },
        received: (data: { action: string, data: object }) => cableService(data, store),
      },
    );
    return true;
  }, [setWidgetSubscription, store, uuid]);

  const requestContactInfo = useCallback(() => {
    const conversationKeys = storeGet(`slc_${window.ChatWidget.token}_dk`) || [];

    if (contactId || (!conversationKeys.length && !userId)) {
      setContactRecognized(true);
      return;
    }

    subscription.send({
      action_name: 'recognize_contact',
      payload: {
        account_id: accountId,
        user_id: userId,
        org_name: orgName,
        conversation_keys: conversationKeys,
      },
    });
  }, [accountId, contactId, orgName, setContactRecognized, subscription, userId]);

  useEffect(() => () => {
    // @ts-ignore
    window.ChatWidget.cable.subscriptions.subscriptions.map((s) => s.unsubscribe());
    setWidgetSubscription(null);
  }, [setWidgetSubscription]);

  useEffect(() => {
    if (subscription && !contactRequested) {
      requestContactInfo();
      setContactRequested(true);
    }
  }, [contactRequested, subscription, requestContactInfo, setContactRequested]);

  const initWSConnection = useCallback(() => {
    subscribeWidgetEvents(window.ChatWidget.cable);
  }, [subscribeWidgetEvents]);

  // return { ready: !!subscription, initWSConnection };
  return {
    initWSConnection,
    connected,
  };
}
